import { Box, Button, Flex, LineChart, Loading, Text } from 'ui'
import { useMemo } from 'react'
import { usePayRateTimeseries } from 'queries/pulse/usePayRateTimeseries'
import { usePulseContext } from '../../context/PulseProvider'
import { formatCurrency } from 'lib/string'
import { groupBy, uniq } from 'lodash'
import { formatTimePeriodDate, getColorScale } from '../../utils'
import { usePayRateTimeseriesDownloadCsv } from 'queries/pulse/usePayRateTimeseriesDownloadCsv'

export function PayRateTimeseries() {
  const {
    state: { filters },
  } = usePulseContext()

  const { data, isLoading } = usePayRateTimeseries(filters)

  const { mutateAsync: downloadCsv, isPending: isDownloadingCsv } =
    usePayRateTimeseriesDownloadCsv(filters)

  const chartSeries = useMemo(() => {
    const positionTypes = uniq(data?.map((item) => item.position_type) ?? [])
    const getColor = getColorScale(positionTypes.length)

    return positionTypes.map((positionType, idx) => ({
      name: positionType,
      color: getColor(idx),
      label: positionType,
    }))
  }, [data])

  const chartData = useMemo(() => {
    const groupedByDate = groupBy(data ?? [], 'shift_date')
    return Object.entries(groupedByDate).map(([date, items]) => {
      return {
        shift_date: formatTimePeriodDate(filters.timePeriod, date),
        ...items.reduce((acc, item) => {
          acc[item.position_type] = item.avg_pay_rate
          return acc
        }, {}),
      }
    })
  }, [data])

  return (
    <Box>
      <Flex
        flexDirection="row"
        justifyContent="space-between"
        alignItems="center"
        gap={3}
        mb={3}
      >
        <Box>
          <Text fontSize={3} fontWeight="500">
            Avg Pay Rate Evolution
          </Text>
          <Text fontSize={2} color="lightText">
            Time evolution of pay rate (effective pay rate - including pro-rated
            bonuses)
          </Text>
        </Box>
        <Button
          variant="secondary"
          onClick={() => downloadCsv()}
          loading={isDownloadingCsv}
        >
          CSV
        </Button>
      </Flex>
      {isLoading ? (
        <Box p={3}>
          <Loading />
        </Box>
      ) : (
        <LineChart
          data={chartData}
          dataKey="shift_date"
          height={450}
          series={chartSeries}
          valueFormatter={formatCurrency}
          withTooltip
          withLegend
          withPointLabels
          withXAxis
          withYAxis
          shouldConnectNulls
        />
      )}
    </Box>
  )
}
